import axios from "axios";
import * as Sentry from "@sentry/react";
import SubscriptionPlan from "../apiResources/SubscriptionPlan";
import {
  LOADING_SUBSCRIPTION_PLANS,
  LOAD_SUBSCRIPTION_PLANS,
  LOAD_SUBSCRIPTION_PLANS_ERROR,
  LOAD_ANNUAL_SUBSCRIPTION_PLANS,
  LOAD_ANNUAL_SUBSCRIPTION_PLANS_ERROR,
  TOGGLE_TERMS_MODAL,
  TOGGLE_PAY_TERMS,
  FETCH_TERMS_OF_BUSINESS,
  FETCH_TERMS_OF_BUSINESS_ERROR,
} from "./types";

export const loadingSubscriptionPlans = () => (dispatch) =>
  dispatch({ type: LOADING_SUBSCRIPTION_PLANS });

const badge = ({ isActive, billingPeriod }) => {
  if (isActive) return "current";

  const isAnnual = billingPeriod === "year";
  if (isAnnual) return "Save over 20%";

  return null;
};

const buttonName = ({
  isActive,
  canUpgradeTo,
  canDowngradeTo,
  downgradable,
  upgradable,
  billingPeriod,
}) => {
  const isAnnual = billingPeriod === "year";
  if (isActive || isAnnual) return "Call to Discuss";
  if (canUpgradeTo) return "Upgrade";
  if (canDowngradeTo) return "Downgrade";
  if (downgradable) return "Call to Enquire";
  if (upgradable) return "Call to Enquire";
  return "Get Started";
};

const oldPrice = ({ price }) => Math.round(1.2 * price);

const termsMonthly =
  "All prices exclude VAT at prevailing rate. 12-month contract, paid monthly. Additional users available at £25 per seat; additional sectors at £30.";
const termsAnnually =
  "Additional users available at £300 per seat, per annum; additional sectors at £360 pa. *Service Fee 0% for first £60,000 of fee income, thereafter a 2.5% Service Fee until the terms end.";

const findCsrfToken = () => {
  let token = document.querySelector("meta[name=csrf-token]");

  if (token !== null) {
    token = token.getAttribute("content");
  } else {
    Sentry.captureException(new Error("csrf-token is missing for memberships"));
    token = "missing_token";
  }

  return token;
};

export const loadMonthlySubscriptionPlans = () => async (dispatch) => {
  try {
    const { data } = await SubscriptionPlan.where({
      billing_period: "month",
    })
      .order({ price: "asc" })
      .all();

    const monthly = data.map(
      ({
        engagementLimitPerMonth,
        handlingFeePercentage,
        sectorsLimit,
        ...attributes
      }) => ({
        sectors: sectorsLimit,
        credits: engagementLimitPerMonth,
        serviceFee: handlingFeePercentage,
        authenticityToken: findCsrfToken(),
        badge: badge(attributes),
        buttonName: buttonName(attributes),
        ...attributes,
      })
    );

    const payload = {
      monthly,
      termsMonthly,
    };

    return dispatch({
      type: LOAD_SUBSCRIPTION_PLANS,
      payload,
    });
  } catch (error) {
    Sentry.captureException(error);

    return dispatch({
      type: LOAD_SUBSCRIPTION_PLANS_ERROR,
      payload: error,
    });
  }
};

export const loadAnnualSubscriptionPlans = () => async (dispatch) => {
  try {
    const { data } = await SubscriptionPlan.where({
      billing_period: "year",
    })
      .order({ price: "asc" })
      .all();

    const csrfToken = document
      .querySelector("meta[name=csrf-token]")
      .getAttribute("content");

    const annually = data.map(
      ({
        engagementLimitPerMonth,
        handlingFeePercentage,
        sectorsLimit,
        ...attributes
      }) => ({
        sectors: sectorsLimit,
        credits: engagementLimitPerMonth,
        serviceFee: handlingFeePercentage,
        authenticityToken: csrfToken,
        badge: badge(attributes),
        buttonName: buttonName(attributes),
        oldPrice: oldPrice(attributes),
        ...attributes,
      })
    );

    const payload = {
      annually,
      termsAnnually,
    };

    return dispatch({
      type: LOAD_ANNUAL_SUBSCRIPTION_PLANS,
      payload,
    });
  } catch (error) {
    Sentry.captureException(error);

    return dispatch({
      type: LOAD_ANNUAL_SUBSCRIPTION_PLANS_ERROR,
      payload: error,
    });
  }
};

export const fetchTermsOfBusiness = () => async (dispatch) => {
  const url = "/recruiter/terms_of_business";

  try {
    const response = await axios.get(url);

    dispatch({
      type: FETCH_TERMS_OF_BUSINESS,
      payload: response.data,
    });
  } catch (error) {
    Sentry.captureException(error);

    dispatch({
      type: FETCH_TERMS_OF_BUSINESS_ERROR,
      payload: {
        message:
          "Failed to load T&Cs. Please try again later or contact your Account Manager.",
      },
    });
  }
};

export const toggleTermsModal = () => (dispatch) => {
  dispatch(fetchTermsOfBusiness());
  dispatch({
    type: TOGGLE_TERMS_MODAL,
  });
};

export const togglePayTermsAgreement = () => (dispatch) => {
  dispatch({
    type: TOGGLE_PAY_TERMS,
  });
};
